import { ChangeDetectionStrategy, Component, OnInit, signal, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MemberStateService } from '../../services/member-state.service';
import { FormatService } from '../../services/format.service';
import { EMPTY, Observable } from 'rxjs';
import { MemberService } from '../../services/member.service';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { MembershipOverview } from '../../model/membership-overview';
import { MemberUpdate } from '../../model/member-update';
import { MessageBoxService } from '../shared/message-box/message-box.service';
import { Member } from '../../model/member';
import { LocalizedText } from '../../model/localized-text';
import { PersonalInformationIdentificationComponent } from './personal-information-identity/personal-information-identification.component';
import { PersonalInformationAddressComponent } from './personal-information-address/personal-information-address.component';
import { PersonalInformationBankAccountComponent } from './personal-information-bankaccount/personal-information-bank-account.component';
import { PersonalInformationContactComponent } from './personal-information-contactinfo/personal-information-contact.component';
import { PersonalInformationContributionComponent } from './personal-information-contribution/personal-information-contribution.component';
import { UserContextService } from '../../services/user-context.service';
import { AsyncPipe } from '@angular/common';
import { ViewState, toViewState } from '../../model/view-state';

import {FIRST_EURO_YEAR} from '../../view/membership-fee.view';
@Component({
    selector: 'myabvv-personal-information',
    templateUrl: './personal-information.component.html',
    styleUrl: './personal-information.component.css',
    standalone: true,
    imports: [
        TranslateModule,
        AsyncPipe,
        PersonalInformationIdentificationComponent,
        PersonalInformationAddressComponent,
        PersonalInformationBankAccountComponent,
        PersonalInformationContactComponent,
        PersonalInformationContributionComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PersonalInformationComponent implements OnInit {
  private memberStateService = inject(MemberStateService);
  private formatService = inject(FormatService);
  private memberService = inject(MemberService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageBoxService = inject(MessageBoxService);
  private userContext = inject(UserContextService);


  member = signal<Member | null>(null);
  membershipState$: Observable<ViewState<MembershipOverview>>;
  startYearOfEURO = FIRST_EURO_YEAR;
  isUpdating = signal(false);
  hasUpdateError = signal(false);
  activeIndex = signal(0);
  openPanels = signal<{ [key: number]: boolean }>({});

  updateServerErrors = signal<{ email?: string; phone?: string; general?: string } | null>(null);

  ngOnInit() {

    this.route.queryParams.subscribe({
      next: params => {
        const tab = Number(params['tab']);
        if (!isNaN(tab) && tab >= 0) {
          this.activeIndex.set(tab);
          this.openPanels.update(panels => ({ ...panels, [tab]: true }));
        } else {
          this.openPanels.update(panels => ({ ...panels, [0]: true })); // Default: first panel open
        }
      },
      error: err => console.error('[PersonalInformationComponent] Query params error:', err)
    });

    this.initializeMembership();
  }

  onUpdateMember(updatedMember: MemberUpdate) {
    this.isUpdating.set(true);
    this.updateServerErrors.set(null);
    this.hasUpdateError.set(false);

    // Use /me endpoint for external users, /{inss} for internal users
    const currentMember = this.member()!;
    const updateMember$ = this.userContext.isInternalUser()
      ? this.memberService.updateMember(currentMember.personalInformation.inss, updatedMember)
      : this.memberService.updateMyMember(updatedMember);

    updateMember$
      .pipe(
        take(1),
        tap((res: Member) => {
          this.member.set(res);
          this.memberStateService.setMember(res);
          this.messageBoxService.showModal('USER_FEEDBACK.dialog_title',
            'USER_FEEDBACK.send_success',
            'USER_FEEDBACK.dialog_ok',
            '',
            '');
        }),
        catchError(err => {
          this.hasUpdateError.set(true);
          this.updateServerErrors.set(err);
          return EMPTY;
        }))
      .subscribe({
        complete: () => {
          this.isUpdating.set(false);
        }
      });
  }

  initializeMembership() {
    const isInternalUser = this.userContext.isInternalUser();

    this.membershipState$ = this.memberStateService.getMember().pipe(
      take(1),
      switchMap((member: Member) => {
        if (typeof member === 'undefined' || member === null) {
          this.router.navigate(['search']).then().catch();
          return EMPTY;
        } else {
          this.member.set(member);
          // Use /me endpoint for external users, /{inss} for internal users
          return isInternalUser
            ? this.memberService.getMemberShip(member.personalInformation.inss)
            : this.memberService.getMyMemberShip();
        }
      }),
      map((res: MembershipOverview) => {
        if (res != null && res.contributionYears != null) {
          res.contributionYears = res.contributionYears
            .filter(contributionsByYear => contributionsByYear.yearOfContribution >= this.startYearOfEURO);
        }
        return res;
      }),
      toViewState()
    );
  }

  getTranslatedData(array: LocalizedText[] | null | undefined): string {
    return this.formatService.getTranslatedData(array);
  }
}
